import { RequireAccess } from '@/components/require-access'
import { useJobillaOrgId, useQuotaRequestMore } from '@/hooks'
import { apiErrorMessage, formatPeriodEnd, useAuth, useEntitlements } from '@/lib'
import { cn } from '@/lib/utils'
import { ConfirmationDialog, type ConfirmationDialogRef, logger } from '@/ui'
import { DocumentTextIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import {
	entitlementIndexQueryKey,
	subscriptionIndexQueryKey,
	type SubscriptionIndexResponse,
	useOrganisationRelationShow,
	useSubscriptionDestroy,
	useSubscriptionIndex,
	useSubscriptionResume,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router'
import { format } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '../../../../components/ui/badge'
import { SubtleLoader } from '../../../../ui'
import { Button } from '../../../rapid-ui'
import { SettingsSubheading } from '../shared'

const STATUS_CLASSNAMES: Record<string, string> = {
	active: 'bg-green-100 text-green-800',
	trialing: 'bg-blue-100 text-blue-800',
	past_due: 'bg-amber-100 text-amber-800',
	canceled: 'bg-red-100 text-red-800',
	incomplete: 'bg-grey-100 text-grey-800',
	incomplete_expired: 'bg-grey-100 text-grey-800',
	paused: 'bg-grey-100 text-grey-800',
	unpaid: 'bg-red-100 text-red-800',
}

const STATUS_LABEL_KEYS: Record<string, string> = {
	active: 'subscription.statusActive',
	trialing: 'subscription.statusTrialing',
	past_due: 'subscription.statusPastDue',
	canceled: 'subscription.statusCanceled',
	incomplete: 'subscription.statusIncomplete',
	incomplete_expired: 'subscription.statusExpired',
	paused: 'subscription.statusPaused',
	unpaid: 'subscription.statusUnpaid',
}

function formatDate(dateString: string | null): string {
	if (!dateString) { return '\u2014' }
	return format(new Date(dateString), 'MMM d, yyyy')
}

const companyRoute = getRouteApi('/_auth/company/$companyId')

export function SubscriptionSection() {
	const { t } = useTranslation('common')
	const { permissions } = useAuth()
	const organisationId = useJobillaOrgId()
	const { companyId } = companyRoute.useParams()
	const navigate = useNavigate()

	const [error, setError] = useState('')

	const { campaignQuota } = useEntitlements()
	const {
		request: requestMoreCampaigns,
		hasSent: quotaRequestSent,
		isDisabled: quotaRequestDisabled,
		isError: quotaRequestError,
	} = useQuotaRequestMore(campaignQuota.periodEnd)

	const {
		data: subscriptionResponse,
		isLoading,
		isError,
		error: queryError,
		refetch,
	} = useSubscriptionIndex(organisationId, {
		enabled: !!organisationId && !!permissions?.includes('billing:read'),
	})

	const parentOrganisation = useOrganisationRelationShow(organisationId, {
		enabled: !!organisationId && !!permissions?.includes('billing:read'),
	}).data?.parent ?? null

	const subscription = subscriptionResponse

	useEffect(() => {
		if (isError && queryError) {
			logger.error(queryError, {
				category: 'billing',
				data: { action: 'fetch_subscription', organisationId },
			})
		}
	}, [isError, queryError, organisationId])

	const queryClient = useQueryClient()
	const confirmDialogRef = useRef<ConfirmationDialogRef>(null)

	const applySubscriptionChange = async (updated: SubscriptionIndexResponse) => {
		queryClient.setQueryData(subscriptionIndexQueryKey(organisationId), updated)
		await queryClient.invalidateQueries({ queryKey: entitlementIndexQueryKey(organisationId) })
	}

	const reportSubscriptionError = (action: string) => (err: unknown) => {
		setError(apiErrorMessage(err) ?? t('subscription.actionFailed'))
		logger.error(err, { category: 'billing', data: { action, organisationId } })
	}

	const cancelMutation = useSubscriptionDestroy({
		onMutate: () => setError(''),
		onSuccess: async (updated) => {
			logger.track('Subscription: cancelled', { category: 'billing' })
			await applySubscriptionChange(updated)
		},
		onError: reportSubscriptionError('cancel_subscription'),
	})

	const resumeMutation = useSubscriptionResume({
		onMutate: () => setError(''),
		onSuccess: async (updated) => {
			logger.track('Subscription: resumed', { category: 'billing' })
			await applySubscriptionChange(updated)
		},
		onError: reportSubscriptionError('resume_subscription'),
	})

	const cancelSubscription = async (profileId: string, planName: string, periodEnd: string | null) => {
		try {
			await confirmDialogRef.current?.confirm({
				title: t('subscription.cancelConfirm.title', { plan: planName }),
				message: t('subscription.cancelConfirm.message', { date: formatDate(periodEnd) }),
				confirmText: t('subscription.cancelConfirm.confirm'),
				cancelText: t('subscription.cancelConfirm.keep'),
				destructive: true,
			})
		} catch {
			return
		}

		cancelMutation.mutate({ organisation: organisationId, profile: profileId })
	}

	const selfServeProfileId =
		subscription?.hasSubscription && subscription.selfServe && subscription.status !== 'canceled'
			? subscription.profileId
			: null

	const statusClassName = subscription?.status ? STATUS_CLASSNAMES[subscription.status] : null
	const statusLabelKey = subscription?.status ? STATUS_LABEL_KEYS[subscription.status] : null
	const canViewInvoices = !!permissions?.includes('invoice:view')

	// Without billing:read the subscription query never runs, so there is no data to
	// show — hide the section rather than render a misleading "no subscription" state.
	if (!permissions?.includes('billing:read')) {
		return null
	}

	return (
		<div className="space-y-8 max-w-[800px]">
			<div>
				<SettingsSubheading>{t('subscription.title')}</SettingsSubheading>
			</div>

			<div className="border border-grey-200 rounded-lg">
				<div className="p-5 space-y-6">
					{isLoading && <SubtleLoader delay={0} />}

					{!isLoading && isError && (
						<div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
							<div className="flex items-start gap-3">
								<ExclamationTriangleIcon className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
								<div>
									<div className="font-medium text-amber-900 mb-1">
										{t('subscription.loadError')}
									</div>
									<div className="text-sm text-amber-700 mb-3">
										{t('subscription.loadErrorDesc')}
									</div>
									<Button type={'neutral'} onClick={() => refetch()}>
										{t('common.tryAgain')}
									</Button>
								</div>
							</div>
						</div>
					)}

					{!isLoading && !isError && !subscription?.hasSubscription && (
						<div className="space-y-6">
							<div className="bg-grey-50 border border-grey-200 rounded-lg p-4">
								<div className="flex items-start gap-3">
									<InformationCircleIcon className="w-5 h-5 text-grey-500 mt-0.5 flex-shrink-0" />
									<div>
										<div className="font-medium text-grey-900 mb-1">
											{t('subscription.noSubscription')}
										</div>
										<div className="text-sm text-grey-600">
											{t('subscription.noSubscriptionDesc')}
										</div>
										{parentOrganisation
											? (
												<p className="mt-3 text-sm text-grey-600">
													{t('subscribe.childOrganisation', {
														parent: parentOrganisation.name,
													})}
												</p>
											)
											: (
												<RequireAccess permission="billing:manage">
													<Link
														to="/company/$companyId/settings/subscribe"
														params={{ companyId }}
														className="mt-3 inline-flex rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
													>
														{t('subscription.choosePlan')}
													</Link>
												</RequireAccess>
											)}
									</div>
								</div>
							</div>
						</div>
					)}

					{!isLoading && !isError && subscription?.hasSubscription && (
						<div className="space-y-6">
							<div className={'flex justify-between'}>
								{subscription.planName
									? (
										<div className={'flex items-start'}>
											<span className="text-md font-bold text-grey-900 mr-3">
												{subscription.planName} {t('subscription.plan')}
											</span>
											{statusClassName && statusLabelKey
												? <Badge className={statusClassName}>{t(statusLabelKey)}</Badge>
												: <span className="text-sm text-grey-900">{'\u2014'}</span>}
										</div>
									)
									: <div />}
								{canViewInvoices && (
									<button
										type="button"
										onClick={() =>
											navigate({
												to: '/company/$companyId/settings/invoices',
												params: { companyId },
											})}
										className="flex items-center gap-1.5 rounded-md border border-grey-200 bg-white px-2.5 py-1 text-xs font-medium text-grey-700 transition-colors hover:border-brand-500 hover:text-brand-700 cursor-pointer"
									>
										<DocumentTextIcon className="h-3.5 w-3.5" />
										{t('settingsNav.invoices')}
									</button>
								)}
							</div>
							{subscription.status === 'past_due' && (
								<div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
									<div className="flex items-start gap-3">
										<ExclamationTriangleIcon className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
										<div>
											<div className="font-medium text-amber-900 mb-1">
												{t('subscription.paymentAttention')}
											</div>
											<div className="text-sm text-amber-700">
												{t('subscription.paymentAttentionDesc')}
											</div>
										</div>
									</div>
								</div>
							)}

							{subscription.status === 'canceled' && (
								<div className="bg-grey-50 border border-grey-200 rounded-lg p-4">
									<div className="flex items-start gap-3">
										<InformationCircleIcon className="w-5 h-5 text-grey-500 mt-0.5 flex-shrink-0" />
										<div>
											<div className="font-medium text-grey-900 mb-1">
												{t('subscription.canceled')}
											</div>
											<div className="text-sm text-grey-600">
												{t('subscription.canceledDesc', {
													date: formatDate(subscription.currentPeriodEnd),
												})}
											</div>
										</div>
									</div>
								</div>
							)}

							<div className="divide-y divide-grey-100">
								<div className="flex items-center justify-between py-3">
									<span className="text-sm text-grey-500">{t('subscription.billingPeriod')}</span>
									<span className="text-sm text-grey-900">
										{formatDate(subscription.currentPeriodStart)} –{' '}
										{formatDate(subscription.currentPeriodEnd)}
									</span>
								</div>
								<div className="flex items-center justify-between py-3">
									<span className="text-sm text-grey-500">{t('subscription.nextBillingDate')}</span>
									<span className="text-sm text-grey-900">
										{formatDate(subscription.nextBillingDate)}
									</span>
								</div>
								{subscription.endsAt && (
									<div className="flex items-center justify-between py-3">
										<span className="text-sm text-grey-500">{t('subscription.endsAt')}</span>
										<span className="text-sm text-grey-900">
											{formatDate(subscription.endsAt)}
										</span>
									</div>
								)}
								{subscription.campaignFeePercentage != null && (
									<div className="flex items-center justify-between py-3">
										<span className="text-sm text-grey-500">
											{t('subscription.campaignServiceFee')}
										</span>
										<span className="text-sm text-grey-900">
											{subscription.campaignFeePercentage}%
										</span>
									</div>
								)}
							</div>

							{subscription.addons.length > 0 && (
								<div className="space-y-2">
									<div className="flex items-center justify-between">
										<span className="text-sm font-medium text-grey-900">
											{t('subscription.addons')}
										</span>
										<span className="text-xs text-grey-500">
											{t('subscription.addonsSubtitle')}
										</span>
									</div>
									<div className="divide-y divide-grey-100">
										{subscription.addons.map((addon, index) => (
											<div key={`${index}-${addon}`} className="py-3 text-sm text-grey-900">
												{addon}
											</div>
										))}
									</div>
								</div>
							)}

							{campaignQuota.limit !== null && (
								<div className="space-y-2">
									<div className="flex items-center justify-between">
										<span className="text-sm font-medium text-grey-900">
											{t('subscription.campaignUsage')}
										</span>
										<span className="text-xs text-grey-500">
											{t('subscription.campaignUsageSubtitle')}
										</span>
									</div>
									<div className="space-y-3">
										<div className="flex items-center gap-3">
											<div className="flex-1 bg-grey-100 rounded-full h-2 overflow-hidden">
												<div
													className={cn(
														'h-full rounded-full transition-all',
														campaignQuota.isNearLimit || campaignQuota.isExhausted
															? 'bg-amber-400'
															: 'bg-blue-400',
													)}
													style={{
														width: `${
															Math.min(
																(campaignQuota.usage / campaignQuota.limit) * 100,
																100,
															)
														}%`,
													}}
												/>
											</div>
											<span className="text-sm font-medium text-grey-900 tabular-nums whitespace-nowrap">
												{t('campaignQuota.summary', {
													used: campaignQuota.usage,
													limit: campaignQuota.limit,
												})}
											</span>
										</div>
										<p className="text-sm text-grey-600">
											{campaignQuota.isExhausted
												? campaignQuota.periodEnd
													? t('campaignQuota.exhaustedWithDate', {
														date: formatPeriodEnd(campaignQuota.periodEnd),
													})
													: t('campaignQuota.exhausted')
												: t('campaignQuota.remaining', {
													count: Math.max(campaignQuota.limit - campaignQuota.usage, 0),
												})}{' '}
											<button
												type="button"
												onClick={requestMoreCampaigns}
												disabled={quotaRequestDisabled}
												className={cn(
													'underline underline-offset-2 hover:no-underline disabled:opacity-50 disabled:no-underline disabled:cursor-default',
													quotaRequestError
														? 'text-red-700'
														: quotaRequestSent
														? 'text-green-700'
														: 'text-grey-600',
												)}
											>
												{quotaRequestError
													? t('campaignQuota.requestFailed')
													: quotaRequestSent
													? t('campaignQuota.requestSent')
													: t('campaignQuota.requestMore')}
											</button>
										</p>
									</div>
								</div>
							)}
						</div>
					)}

					{error && (
						<div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
							<div className="font-medium text-amber-900 mb-1">
								{t('subscription.errorTitle')}
							</div>
							<div className="text-amber-700">{error}</div>
						</div>
					)}
				</div>

				{!isLoading && !isError && subscription && selfServeProfileId && (
					<div className="flex items-center justify-between gap-4 rounded-b-lg border-t border-grey-200 px-5 py-5 animate-gradient from-gray-50 to-gray-100 bg-gradient-to-r">
						<p className="text-sm text-grey-600">{t('subscription.selfServeDesc')}</p>
						<div className="flex items-center gap-2 shrink-0">
							<RequireAccess
								permission={'billing:manage'}
								fallback={<span className="text-sm text-grey-500">{t('subscription.adminOnly')}</span>}
							>
								{subscription.endsAt
									? (
										<Button
											type="neutral"
											size="tiny"
											onClick={() =>
												resumeMutation.mutate({
													organisation: organisationId,
													profile: selfServeProfileId,
												})}
											isLoading={resumeMutation.isPending}
											disabled={resumeMutation.isPending}
										>
											{t('subscription.resume')}
										</Button>
									)
									: (
										<Button
											type="destructive"
											size="tiny"
											onClick={() =>
												cancelSubscription(
													selfServeProfileId,
													subscription.planName ?? '',
													subscription.currentPeriodEnd,
												)}
											isLoading={cancelMutation.isPending}
											disabled={cancelMutation.isPending}
										>
											{t('subscription.cancel')}
										</Button>
									)}
							</RequireAccess>
						</div>
					</div>
				)}
			</div>
			<ConfirmationDialog ref={confirmDialogRef} />
		</div>
	)
}
